import { Fragment, type ReactNode } from 'react';

import { EmptyState, Spinner } from './primitives.js';

/**
 * A short, already-decided list of rows.
 *
 * WHY THIS IS NOT `DataTable` WITH OPTIONAL PROPS
 * `DataTable` now requires `sorting`, `onSortingChange` and `pager`, and its
 * header explains why at length: sorting a truncated window client-side ranked
 * whichever rows survived the row cap and answered confidently. Making those
 * props optional would reopen exactly that door.
 *
 * This component's contract is the opposite and much narrower: the SERVER has
 * already decided which N rows these are and in what order. There is nothing to
 * sort, nothing to page, and a pager under five rows on a dashboard summary is
 * noise. When a reader wants the whole list they follow the panel's link to the
 * screen that owns it.
 *
 * Same two-shape rule as `DataTable` (design-guidelines §11.2): a real table at
 * `md` and up, stacked cards below. The narrow viewport gets a different shape,
 * not a smaller one.
 */
export interface MiniColumn<T> {
  key: string;
  header: string;
  align?: 'left' | 'right';
  /** Omit from the mobile cards — for columns that exist to be scanned across rows. */
  mobileHidden?: boolean;
  /** The one column that becomes the card heading. Defaults to the first. */
  mobileTitle?: boolean;
  render: (row: T) => ReactNode;
}

/** A run of rows under a sub-heading — see `groups`. */
export interface MiniGroup<T> {
  key: string;
  /** The sub-heading. Null draws none. */
  label: string | null;
  rows: T[];
}

/** What the `#` column shows instead of the row's position — see `rowNumber`. */
export interface MiniRowNumber<T> {
  /** What the number means, for a screen reader; "#" alone says nothing. */
  label: string;
  render: (row: T) => ReactNode;
}

export function MiniTable<T>({
  rows,
  groups,
  groupEmpty,
  rowNumber,
  columns,
  rowKey,
  isLoading,
  loadingLabel = 'Memuat…',
  empty,
  footer,
}: {
  rows?: T[];
  /**
   * The rows split under sub-headings, in place of `rows`.
   *
   * For a table whose rows belong to something the reader counts by — the
   * visit dialog's scores and prices, one heading per photograph. The position
   * count restarts in each group. `empty` still shows when every group is.
   */
  groups?: MiniGroup<T>[];
  /** The line under a heading whose group has no rows. */
  groupEmpty?: ReactNode;
  /**
   * The `#` column's content, in place of the row's position.
   *
   * For a row that has a number of its own elsewhere on screen — a box on a
   * photograph — which a position count would contradict.
   */
  rowNumber?: MiniRowNumber<T>;
  columns: MiniColumn<T>[];
  rowKey: (row: T) => string;
  isLoading?: boolean;
  loadingLabel?: string;
  empty: ReactNode;
  /** e.g. "Menampilkan 5 dari 42" — a sentence, never a pager. */
  footer?: ReactNode;
}) {
  if (isLoading) {
    return (
      <div className="p-6">
        <Spinner label={loadingLabel} />
      </div>
    );
  }

  const sections: MiniGroup<T>[] = groups ?? [{ key: 'rows', label: null, rows: rows ?? [] }];

  if (sections.every((section) => section.rows.length === 0)) {
    return typeof empty === 'string' ? <EmptyState title={empty} /> : <>{empty}</>;
  }

  const titleColumn = columns.find((column) => column.mobileTitle) ?? columns[0];

  return (
    <div>
      {/*
        No `overflow-x-auto`: it would compute `overflow-y` to `auto` too and the
        sticky header below would have a scrollport with no scroll range to stick
        in. See the long note in `DataTable`. These tables are five columns of
        summary, so there is nothing to scroll sideways anyway.
      */}
      <div className="hidden md:block">
        {/* `border-separate` so the header corners can take a radius — see `DataTable`. */}
        <table className="w-full border-separate border-spacing-0 text-sm">
          {/*
            Frozen under whatever floats above the scroll container this lands
            in: the 4rem `TopBar` on a page, nothing at all inside a `Dialog`.
            The container sets `--sticky-header-top`; see the note in
            `index.css`.
          */}
          <thead className="sticky top-[var(--sticky-header-top)] z-20">
            <tr>
              <th
                scope="col"
                className="w-10 whitespace-nowrap rounded-tl-2xl bg-line px-3 py-2 text-right text-xs font-bold uppercase tracking-[0.08em] text-steel shadow-[0_2px_4px_-2px_rgba(19,42,74,0.16)]"
              >
                <span aria-hidden>#</span>
                <span className="sr-only">{rowNumber ? rowNumber.label : 'Nomor urut'}</span>
              </th>
              {columns.map((column, index) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`whitespace-nowrap bg-line px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] text-steel shadow-[0_2px_4px_-2px_rgba(19,42,74,0.16)] ${
                    index === columns.length - 1 ? 'rounded-tr-2xl' : ''
                  } ${column.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          {/* One `tbody` per group, so a group is a real row group to assistive tech. */}
          {sections.map((section) => (
            <tbody key={section.key}>
              {section.label !== null ? (
                <tr>
                  <th
                    scope="colgroup"
                    colSpan={columns.length + 1}
                    className="border-b border-line bg-panel px-3 py-1.5 text-left text-xs font-bold uppercase tracking-[0.08em] text-steel"
                  >
                    {section.label}
                  </th>
                </tr>
              ) : null}
              {section.label !== null && section.rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="border-b border-line px-3 py-2 text-sm text-muted">
                    {groupEmpty}
                  </td>
                </tr>
              ) : null}
              {section.rows.map((row, index) => (
                <tr
                  key={rowKey(row)}
                  className={`${index % 2 === 1 ? 'bg-tint' : ''} [&:last-child>td]:border-b-0`}
                >
                  {/*
                    1..n by default. These panels show a server-chosen top-N with
                    no pager, so there is no later page for the count to continue
                    into — unlike `DataTable`, where it must.
                  */}
                  <td className="whitespace-nowrap border-b border-line px-3 py-2 text-right align-middle tabular-nums text-muted">
                    {rowNumber ? rowNumber.render(row) : index + 1}
                  </td>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`border-b border-line px-3 py-2 align-middle ${
                        column.align === 'right' ? 'text-right tabular-nums' : ''
                      }`}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      <div className="flex flex-col gap-stack p-3 md:hidden">
        {sections.map((section) => (
          <Fragment key={section.key}>
            {section.label !== null ? (
              <div>
                <p className="eyebrow">{section.label}</p>
                {section.rows.length === 0 ? <p className="mt-1 text-sm text-muted">{groupEmpty}</p> : null}
              </div>
            ) : null}
            {section.rows.length > 0 ? (
              <ul className="flex flex-col gap-stack">
                {section.rows.map((row, index) => (
                  <li key={rowKey(row)} className="rounded-2xl border border-line bg-white p-4">
                    {/* A `<div>` rather than a `<p>`, for the reason `DataTable` sets out:
                        this slot renders a column's own `render`, which the desktop
                        layout drops into a `<td>`, and `<p>` accepts phrasing content
                        only. No column here returns a block today — this is the same
                        wrapper, kept the same shape so the next one that does is not a
                        console warning. */}
                    {titleColumn ? (
                      <div className="flex items-baseline gap-2 font-display font-bold text-navy">
                        <span className="shrink-0 text-sm font-semibold tabular-nums text-muted">
                          {rowNumber ? rowNumber.render(row) : `${index + 1}.`}
                        </span>
                        <span className="min-w-0">{titleColumn.render(row)}</span>
                      </div>
                    ) : null}
                    {/*
                      ONE FLEX ROW PER FIELD, NOT A TWO-COLUMN GRID — the same
                      correction `DataTable` already carries, and for the same
                      reason. A grid sizes its label column to the LONGEST label
                      in the card, so every value in the visit dialog's SKU card
                      got whatever "Rencana → aktual" left over, and the four
                      grade badges came out stacked one per line.

                      Wrapped rows are sized one at a time: a short value stays
                      on its label's line at the right edge, and one too wide for
                      what is left drops to a line of its own with the card's
                      full width to wrap in — still right-aligned, so every value
                      in the card shares one edge. `shrink-0` on the label so it
                      is always the value that moves.

                      A value that lays itself out with a BLOCK FLEX ROW ignores
                      the `text-right` here and has to say `justify-end` /
                      `items-end` itself, below `md` only. See the visit dialog's
                      "Nilai" and the planogram photos' grades.
                    */}
                    <dl className="mt-2 flex flex-col gap-1 text-sm">
                      {columns
                        .filter((column) => column !== titleColumn && !column.mobileHidden)
                        .map((column) => (
                          <div
                            key={column.key}
                            className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5"
                          >
                            <dt className="shrink-0 text-muted">{column.header}</dt>
                            <dd className="ml-auto min-w-0 max-w-full text-right text-ink">
                              {column.render(row)}
                            </dd>
                          </div>
                        ))}
                    </dl>
                  </li>
                ))}
              </ul>
            ) : null}
          </Fragment>
        ))}
      </div>

      {footer ? (
        <p className="border-t border-line px-3 py-2 text-sm text-muted">{footer}</p>
      ) : null}
    </div>
  );
}
